import {getAllTranscriptItemObjects, updateAiSubtitleState} from '@Components/poster-editor/poster-editor-reducer';
import {AiTranscriptAction} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {GeneratedFromType, TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {
  addNewTranscriptToPosterForGeneratedAiTranscript,
  doesTranscriptForItemNeedRegeneration,
  generateAiTranscriptAndGetSubtitles,
  NO_SPEECH_DETECTED_ERROR_CODE,
  replaceExistingTranscriptOnPosterForGeneratedAiTranscript,
} from '@Libraries/ai-transcript.library';
import {v4 as uuidv4} from 'uuid';
import {AiSubtitleState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';
import {hideLoading, showLoading, updateLoadingProgress, updateLoadingText} from '@Libraries/loading-toast-library';
import {addAiSubtitlesGenerationResults, setAiSubtitlesGenerationsBatch, updateCurrentNumberOfCompletedAiSubtitleGenerations} from '@Panels/ai-subtitle-panel/ai-subtitle-reducer';
import {AI_SUBTITLES_GENERATION_LOADING_KEY, AiSubtitleItemResult} from '@Panels/ai-subtitle-panel/components/ai-subtitle-generating/ai-subtitle-generating.types';
import {AI_SUBTITLE_PANEL_ID} from '@Panels/ai-subtitle-panel/ai-subtitle-panel.types';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

interface UseAiTranscriptGenerationResultsObject {
  getTranscriptItemObjectGeneratedFromUid: (uid: string) => TranscriptItemObject | undefined;
  getAiSubtitlesActionForItem: (params: AiTranscriptForItemWithParams) => AiTranscriptAction;
  addAiTranscripts: (itemsBatchToAddTranscriptsFor: AiTranscriptForItemWithParams[], onError?: (id: string, error: Error) => void, doSetBatchSize?: boolean) => Promise<void>;
}

export interface AiTranscriptForItemWithParams {
  type: GeneratedFromType;
  hashedFilename: string;
  uid?: string;
  startTime: number;
  endTime: number;
  onPosterStartTime: number;
  loopCycles: number;
  speed: number;
}

export function useAiTranscriptGeneration(): UseAiTranscriptGenerationResultsObject {
  const dispatch = useAppDispatch();
  const allTranscriptItemObjects = useAppSelector(getAllTranscriptItemObjects);
  const generationLanguage = useAppSelector((state) => {
    return state.aiSubtitle.aiSubtitlesPanel.inputLanguage;
  });

  const getTranscriptItemObjectGeneratedFromUid = (uid?: string): TranscriptItemObject | undefined => {
    if (!uid) {
      return undefined;
    }

    return Object.values(allTranscriptItemObjects).find((transcriptItem) => {
      return transcriptItem.generatedFrom?.uid === uid;
    });
  };

  const getAiSubtitlesActionForItem = (params: AiTranscriptForItemWithParams): AiTranscriptAction => {
    const existingTranscriptItemForVideoWithUid = getTranscriptItemObjectGeneratedFromUid(params.uid);
    if (!existingTranscriptItemForVideoWithUid) {
      return AiTranscriptAction.GENERATE;
    }

    return doesTranscriptForItemNeedRegeneration(params, existingTranscriptItemForVideoWithUid) ? AiTranscriptAction.REGENERATE : AiTranscriptAction.GENERATED;
  };

  const addAiTranscripts = async (
    itemsBatchToAddTranscriptsFor: AiTranscriptForItemWithParams[],
    onError?: (id: string, error: Error) => void,
    doSetBatchSize = true
  ): Promise<void> => {
    const poster = window.posterEditor?.whiteboard;
    if (!poster) {
      return;
    }

    const totalCountOfItemsInBatch = itemsBatchToAddTranscriptsFor.length;
    let successCount = 0;

    let earliestTranscriptId = '';
    let earliestOnPosterStartTime: number | undefined;

    let index = 0;

    const currentPage = poster.getCurrentPage();

    if (doSetBatchSize) {
      dispatch(setAiSubtitlesGenerationsBatch(totalCountOfItemsInBatch));
    }
    dispatch(updateAiSubtitleState(AiSubtitleState.GENERATING));
    showLoading(AI_SUBTITLES_GENERATION_LOADING_KEY, {text: window.i18next.t('pmwjs_generating_ai_subtitles'), progress: 0});

    for (const itemToAddTranscriptFor of itemsBatchToAddTranscriptsFor) {
      const newTranscriptItemUid = uuidv4();
      const {onPosterStartTime} = itemToAddTranscriptFor;

      if (earliestOnPosterStartTime === undefined || onPosterStartTime < earliestOnPosterStartTime) {
        earliestOnPosterStartTime = onPosterStartTime;
        earliestTranscriptId = newTranscriptItemUid;
      }

      dispatch(updateCurrentNumberOfCompletedAiSubtitleGenerations(index + 1));
      updateLoadingProgress(AI_SUBTITLES_GENERATION_LOADING_KEY, (index + 1) / itemsBatchToAddTranscriptsFor.length);
      updateLoadingText(AI_SUBTITLES_GENERATION_LOADING_KEY, `${window.i18next.t('pmwjs_generating')} (${index + 1} of ${itemsBatchToAddTranscriptsFor.length})`);

      // eslint-disable-next-line no-await-in-loop
      await addAiTranscriptForItem(
        newTranscriptItemUid,
        itemToAddTranscriptFor,
        false,
        () => {
          successCount += 1;
        },
        onError
      );
      index += 1;
    }

    hideLoading(AI_SUBTITLES_GENERATION_LOADING_KEY);

    if (successCount === totalCountOfItemsInBatch) {
      dispatch(updateAiSubtitleState(AiSubtitleState.READY_SUCCESS));
    } else {
      dispatch(updateAiSubtitleState(AiSubtitleState.READY_FAILED));
    }

    if (successCount === totalCountOfItemsInBatch) {
      const transcript = currentPage.items.itemsHashMap[earliestTranscriptId] as TranscriptItem;
      if (transcript) {
        currentPage.activeSelection.selectFabricObjects([transcript.fabricObject]);

        const earliestSubtitle = transcript.getSubtitleIdsInOrder()[0];
        void poster.seek(transcript.subtitlesHashmap[earliestSubtitle].startTime);
      }
    }
  };

  const addAiTranscriptForItem = async (
    uidForTranscriptToAdd: string,
    params: AiTranscriptForItemWithParams,
    selectOnAdd: boolean,
    onSuccess: () => void,
    onError?: (id: string, error: Error) => void
  ): Promise<void> => {
    const action = getAiSubtitlesActionForItem(params);

    if (action === AiTranscriptAction.GENERATED) {
      return;
    }

    try {
      trackPosterBuilderGA4Events(GA4EventName.AI_SUBTITLE_GENERATE, {[GA4EventParamName.TYPE]: params.type});

      const transcript = await generateAiTranscriptAndGetSubtitles(
        params.hashedFilename,
        params.startTime,
        params.endTime,
        params.type,
        params.loopCycles,
        params.speed,
        generationLanguage
      );

      onSuccess();
      dispatch(addAiSubtitlesGenerationResults({id: params.uid ?? '', result: AiSubtitleItemResult.SUCCESS}));

      if (action === AiTranscriptAction.GENERATE) {
        await addNewTranscriptToPosterForGeneratedAiTranscript(
          uidForTranscriptToAdd,
          transcript,
          params.onPosterStartTime,
          {
            uid: params.uid,
            type: params.type,
            hashedFilename: params.hashedFilename,
            startTime: params.startTime,
            endTime: params.endTime,
            onPosterStartTime: params.onPosterStartTime,
            loopCycles: params.loopCycles,
            speed: params.speed,
          },
          selectOnAdd
        );
      } else if (action === AiTranscriptAction.REGENERATE) {
        const existingTranscriptItem = getTranscriptItemObjectGeneratedFromUid(params.uid);
        if (!existingTranscriptItem) {
          return;
        }

        await replaceExistingTranscriptOnPosterForGeneratedAiTranscript(
          existingTranscriptItem,
          uidForTranscriptToAdd,
          transcript,
          params.onPosterStartTime,
          {
            uid: params.uid,
            type: params.type,
            hashedFilename: params.hashedFilename,
            startTime: params.startTime,
            endTime: params.endTime,
            onPosterStartTime: params.onPosterStartTime,
            loopCycles: params.loopCycles,
            speed: params.speed,
          },
          selectOnAdd
        );
      }
    } catch (error: any) {
      let status = AiSubtitleItemResult.RETRY;
      if (error.message === NO_SPEECH_DETECTED_ERROR_CODE) {
        status = AiSubtitleItemResult.FAILURE;
      } else {
        // console.error(error);
      }

      dispatch(addAiSubtitlesGenerationResults({id: params.uid ?? '', result: status}));

      const isAiSubtitlesPanelOpen = window.PMW.redux.store.getState().panels.panelHashmap[AI_SUBTITLE_PANEL_ID];
      if (isAiSubtitlesPanelOpen === undefined) {
        showMessageGrowl({
          text: window.i18next.t('pmwjs_ai_subtitles_generating_error'),
          type: GROWL_TYPE.DANGER,
          interval: 15000,
        });
      }

      if (onError) {
        onError(params.uid ?? '', error);
      }
    }
  };

  return {
    getTranscriptItemObjectGeneratedFromUid,
    getAiSubtitlesActionForItem,
    addAiTranscripts,
  };
}
